import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { MultiPropertyCard } from "@/components/generic/multi-property-card";
import { MultiPropertyCardList } from "@/components/generic/multi-property-card-list";

import { getExecutedJobsProperties } from "@/components/milling-jobs/executed-job-list";
import { type ApiError, isApiWaitingOrError } from "@/data-interfaces/generics";
import type {
	ExecutedMillingJob,
	ExecutedMillingJobDetails,
} from "@/data-interfaces/milling-jobs";
import { useMQTTAutoRefreshPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import { getJobBlocks } from "@/lib/data-remappings/executed-job-details";
import {
	EXECUTED_JOB_DETAILS_PAGE_URL,
	getAppWrappedPageUrl,
	MILLING_JOBS_PAGE_URL,
} from "@/lib/page-routes";
import { MJ_EXECUTED_JOB_DETAILS_URL } from "@/lib/request-paths";

export const Route = createFileRoute(
	getAppWrappedPageUrl(EXECUTED_JOB_DETAILS_PAGE_URL),
)({
	component: ExecutedJobDetails,
	validateSearch: (search: ExecutedMillingJob) => {
		search.start_date = new Date(search.start_date);
		return search;
	},
});

function ExecutedJobDetails() {
	const [t, _] = useTranslation();
	const [tn, __] = useTranslation("notifications");

	const navigate = useNavigate();

	const executedJob = Route.useSearch();

	const [jobDetails, setJobDetails] = useState<
		ExecutedMillingJobDetails | ApiError | null
	>(null);

	useMQTTSubscribe(MJ_EXECUTED_JOB_DETAILS_URL, setJobDetails);

	useMQTTAutoRefreshPublish(0, MJ_EXECUTED_JOB_DETAILS_URL, {
		job_id: executedJob.id,
	});

	return (
		<div className="text-white p-8 flex flex-col flex-1 gap-y-4">
			<h2 className="text-2xl sm:text-3xl hover:text-[#238b8c]">
				<button
					type={"button"}
					onClick={() => {
						navigate({
							to: MILLING_JOBS_PAGE_URL,
							search: () => ({ selectedTab: "history" }),
						});
					}}
				>
					{"< "}
					{t("Jobs")}
				</button>
			</h2>

			<div className="flex flex-col gap-y-4">
				<h2 className="text-2xl sm:text-3xl">{t("Selected job")}</h2>
				<MultiPropertyCard
					multiPropertyObject={{
						title: executedJob.name,
						properties: getExecutedJobsProperties(executedJob),
					}}
				/>
			</div>

			<div className="flex flex-col flex-1 grow gap-y-4">
				<h2 className="text-2xl sm:text-3xl">{t("Job details")}</h2>

				{!isApiWaitingOrError(jobDetails) && jobDetails.error && (
					<div className="text-red-400 text-2xl">
						{tn(jobDetails.error.key, jobDetails.error.context)}
					</div>
				)}

				<MqttSuspense
					objects={jobDetails}
					requestUrl={MJ_EXECUTED_JOB_DETAILS_URL}
					parameters={{ job_id: executedJob.id }}
					genericError="Error retrieving job details"
				>
					{(jobDetails) => (
						<MultiPropertyCardList
							multiPropertyObjects={getJobBlocks(jobDetails)}
							noItemsLabel="No job block for executed job"
							pagination={true}
						/>
					)}
				</MqttSuspense>
			</div>
		</div>
	);
}
